/**
 * Allowlist Knowledge origins for Oak → KS auth handoff redirects.
 */

const LOCAL_KNOWLEDGE_ORIGIN = "http://localhost:4100";
const PROD_KNOWLEDGE_ORIGIN = "https://knowledge.encamp.com";

export function knowledgeAuthContinueStorageKey(): string {
  return "ks_auth_continue";
}

export type KnowledgeAuthContinueParams = {
  returnTo: string;
  state: string;
  knowledgeOrigin: string;
};

function isHttpOrigin(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Origins allowed as Knowledge handoff targets (open-redirect guard).
 */
export function allowedKnowledgeOrigins(
  env: ImportMetaEnv | { VITE_KNOWLEDGE_PUBLIC_URL?: string } = import.meta.env
): string[] {
  const origins = new Set<string>([LOCAL_KNOWLEDGE_ORIGIN, PROD_KNOWLEDGE_ORIGIN]);
  const fromEnv = env.VITE_KNOWLEDGE_PUBLIC_URL?.trim();
  if (fromEnv != null && fromEnv.length > 0) {
    try {
      origins.add(new URL(fromEnv).origin);
    } catch {
      // ignore invalid env
    }
  }
  return [...origins];
}

export function isAllowedKnowledgeOrigin(
  origin: string,
  env: ImportMetaEnv | { VITE_KNOWLEDGE_PUBLIC_URL?: string } = import.meta.env
): boolean {
  if (!isHttpOrigin(origin)) {
    return false;
  }
  const normalized = new URL(origin).origin;
  return allowedKnowledgeOrigins(env).includes(normalized);
}

/**
 * Parse and validate continue query/session params.
 * `return_to` must be a relative explore path; `knowledge_origin` must be allowlisted.
 */
export function parseKnowledgeAuthContinueParams(input: {
  returnTo: string | null | undefined;
  state: string | null | undefined;
  knowledgeOrigin: string | null | undefined;
  env?: ImportMetaEnv | { VITE_KNOWLEDGE_PUBLIC_URL?: string };
}): KnowledgeAuthContinueParams | null {
  const returnTo = (input.returnTo ?? "").trim();
  const state = (input.state ?? "").trim();
  const knowledgeOriginRaw = (input.knowledgeOrigin ?? "").trim();
  if (returnTo.length === 0 || state.length === 0 || knowledgeOriginRaw.length === 0) {
    return null;
  }
  if (returnTo.startsWith("/") === false || returnTo.startsWith("//")) {
    return null;
  }
  if (returnTo.includes("://") || returnTo.includes("\\")) {
    return null;
  }
  let knowledgeOrigin: string;
  try {
    knowledgeOrigin = new URL(knowledgeOriginRaw).origin;
  } catch {
    return null;
  }
  if (!isAllowedKnowledgeOrigin(knowledgeOrigin, input.env ?? import.meta.env)) {
    return null;
  }
  return { returnTo, state, knowledgeOrigin };
}

export function apiBaseUrlForHandoff(): string {
  if (import.meta.env.MODE === "development") {
    return "http://localhost:4000";
  }
  return import.meta.env.VITE_API_URL;
}
